import { useEffect, useMemo, useState } from "react";

// Data
import { getCountriesList } from "@/data/countries";
import { useFormikContext } from "formik";
import { Client } from "../ClientsDialog";

// Formatter
import { getZipLabel } from "@/components/addresses/AddressLookup/helpers";
import { FieldName } from "@shopify/address";

// Fields
import AddressLookup from "@/components/addresses/AddressLookup";
import { type Address } from "types";
import ComboBoxField from "./ComboBoxField";
import InputField from "./InputField";

interface FormikFieldHandler {
  label: string;
  name: string;
  defaultCountry: string;
  classes?: string;
  hideLabel?: boolean;
  required?: boolean;
  disabled?: boolean;
  helptext?: string;
  placeholder?: string;
  inline?: boolean;
  marketId?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export default function AddressField(props: FormikFieldHandler) {
  let { label, name, classes, hideLabel, inline, marketId } = props;

  // Formik
  const { values, setFieldValue, validateForm } = useFormikContext<Client>();

  // States
  const [country, setCountry] = useState<string>(props.defaultCountry);
  const [fields, setFields] = useState<any[]>([]);
  const [zip, setZip] = useState<string>("");

  // Address Formatter
  // const addressFormatter = new AddressFormatter("en");
  const countries = getCountriesList();

  useEffect(() => {
    if (values.country !== null && values.country !== undefined) {
      setCountry(values.country);
    }
  }, [values]);

  useEffect(() => {
    const fetchFields = async () => {
      // // Get the fields for the country
      // let fields = await addressFormatter.getOrderedFields(
      //   country.substring(0, 2)
      // );

      // console.log({ fields });
      // const notNeededFields = ["firstName", "lastName", "phone", "company"];

      // // We don't need firstName and lastName fields as they are already provided
      // fields = fields.map((field: FieldName[]) => {
      //   // Remove not needed fields
      //   return field.filter((f) => !notNeededFields.includes(f));
      // });

      // // If there are empty rows, remove them
      // fields = fields.filter((field) => field.length > 0);

      // Set the fields
      setFields([["address2"], ["city"], ["province"], ["country"]]);
    };

    fetchFields();
  }, [country]);

  const countryOptions = useMemo(() => {
    // Convert the object
    return Object.entries(countries).map(([key, value]) => {
      return { label: value, value: key };
    });
  }, [countries]);

  return (
    <div className="relative w-full mt-2">
      <div className="flex flex-col w-full gap-2 rounded-lg">
        <ComboBoxField
          key={"country"}
          name={"country"}
          label={"country"}
          options={countryOptions ?? []}
        />
        <InputField
          name={"zip"}
          label={getZipLabel(country)}
          type="text"
          onBlur={(e: any) => {
            // Store the zip code for onBlur event
            setZip(e.target.value);
          }}
          onChange={(e: any) => {
            const newValue = e.target.value;
            setFieldValue("zip", newValue);
            if (!newValue) {
              setFieldValue("coordinates", null);
            }
          }}
        />

        <AddressLookup
          zip={zip}
          country={values.country ?? ""}
          marketId={marketId ?? ""}
          address1={values.address1 ?? ""}
          onChange={(address1: string) => {
            setFieldValue("address1", address1);
            // Clear coordinates if address1 is cleared
            if (!address1) {
              setFieldValue("coordinates", null);
            }
          }}
          onSelect={(values: Partial<Address>) => {
            // Set the values
            setFieldValue("address1", values.address1);
            setFieldValue("address2", values.address2);
            setFieldValue("city", values.city);
            setFieldValue("province", values.province);
            setFieldValue("zip", values.zip);

            if (values.company) {
              setFieldValue("company", values.company);
            }

            if (values.country) {
              setFieldValue("country", values.country);
            }

            if (values.coordinates) {
              setFieldValue("coordinates", values.coordinates);
            }
          }}
        />

        {fields.map((fields: FieldName[]) => {
          return (
            <div className="grid w-full gap-2 -mt-1" key={fields.join("-")}>
              {fields?.map((field: string, index: number) => {
                if (
                  field === "zip" ||
                  field === "country" ||
                  field === "address1"
                ) {
                  return null;
                }

                let label = field;
                let disabled = false;

                if (field === "province") {
                  label = "County";
                }
                return (
                  <InputField
                    key={field}
                    name={field}
                    label={label}
                    type="text"
                    disabled={disabled}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Display Coordinates */}

      <div className="mt-2 text-xs text-gray-400 float-right">
        {values.coordinates ? (
          <>
            <strong>Coordinates:</strong> {values.coordinates.latitude},
            {values.coordinates.longitude}
          </>
        ) : (
          <strong> </strong>
        )}
      </div>
    </div>
  );
}
